// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/bancoIntegracao/hooks/useBancoIntegracaoProvider.ts
//
//  Dado o id de uma conta corrente, busca a configuração de integração e resolve
//  o provider bancário correspondente (Inter hoje; outros bancos amanhã). As
//  telas usam `provider` + `provider.capabilities` sem saber qual banco é.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';
import type { ContaCorrenteIntegracao } from '@/features/financeiro/types';
import type { BancoIntegracaoProvider } from '../provider';
import { resolveProvider } from '../providers';

interface Estado {
  provider: BancoIntegracaoProvider | null;
  integracao: ContaCorrenteIntegracao | null;
  carregando: boolean;
  erro: string | null;
}

export function useBancoIntegracaoProvider(contaCorrenteId: number | null): Estado {
  const [estado, setEstado] = useState<Estado>({
    provider: null, integracao: null, carregando: false, erro: null,
  });

  useEffect(() => {
    if (!contaCorrenteId) {
      setEstado({ provider: null, integracao: null, carregando: false, erro: null });
      return;
    }

    let ativo = true;
    setEstado((s) => ({ ...s, carregando: true, erro: null }));

    cadastrosFinanceirosService.getIntegracaoContaCorrente(contaCorrenteId)
      .then((integracao) => {
        if (!ativo) return;
        setEstado({
          provider: resolveProvider(integracao),
          integracao,
          carregando: false,
          erro: null,
        });
      })
      .catch(() => {
        if (!ativo) return;
        // Sem configuração de integração para a conta → provider nulo (a tela
        // mostra orientação), não um erro bloqueante.
        setEstado({ provider: null, integracao: null, carregando: false, erro: null });
      });

    return () => { ativo = false; };
  }, [contaCorrenteId]);

  return estado;
}
